import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { addAppScrollListener, getAppScrollElement, getAppScrollTop, getAppViewport } from '../utils/appViewport';
import SavedEmojiFilter from './SavedEmojiFilter';
import './SavedBrowsingHeader.css';

const COLLAPSE_DISTANCE = 56;
const EXPAND_DISTANCE = 24;
const COLLAPSE_MIN_Y = 96;
const EXPAND_GRACE_MS = 800;

const hasKeyboardFocus = element => element.contains(document.activeElement)
    && document.activeElement.matches(':focus-visible');

export default function SavedBrowsingHeader({ contentType, listId, listName, savedLists = [], onListChange, actions, children, selectionCount = null, controlsDisabled = false, onBack }) {
    const headerRef = useRef(null);
    const controlsRef = useRef(null);
    const revealButtonRef = useRef(null);
    const revealControlsRef = useRef(null);
    const focusControlsOnReveal = useRef(false);
    const ignoreScrollUntilRef = useRef(0);
    const controlsId = useId();
    const [collapsed, setCollapsed] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const selecting = selectionCount !== null;
    const changeFilterOpen = useCallback(open => {
        ignoreScrollUntilRef.current = Date.now() + EXPAND_GRACE_MS;
        setFilterOpen(open);
    }, []);

    useLayoutEffect(() => {
        const header = headerRef.current;
        const content = header.closest('.savedTracksContent');
        const selectionBar = content?.querySelector('.savedSelectionBar');
        const measure = () => {
            // Keep the reading space constant while the dock minimizes. The action
            // bar owns its own bottom safe area and reservation during selection.
            content?.style.setProperty('--saved-dock-height', `${controlsRef.current.offsetHeight}px`);
            header.style.setProperty('--saved-actions-height', `${selectionBar?.offsetHeight || 0}px`);
        };
        measure();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        observer?.observe(controlsRef.current);
        if (selectionBar) observer?.observe(selectionBar);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
            content?.style.removeProperty('--saved-dock-height');
        };
    }, [selecting]);

    useEffect(() => {
        if (!collapsed && focusControlsOnReveal.current) {
            focusControlsOnReveal.current = false;
            const controls = controlsRef.current;
            const focusTarget = controls.querySelector('button[aria-pressed="true"]:not([disabled])')
                || controls.querySelector('button:not([disabled])');
            focusTarget?.focus({ preventScroll: true });
        }
    }, [collapsed]);

    useEffect(() => {
        const controls = controlsRef.current;
        const measure = () => {
            const maxY = Math.max(0, getAppScrollElement().scrollHeight - getAppViewport().height);
            // Clamp elastic overscroll so bouncing cannot create scroll intent.
            return { y: Math.min(maxY, Math.max(0, getAppScrollTop())), maxY };
        };
        const initialPosition = measure();
        let previousY = initialPosition.y;
        let previousMaxY = initialPosition.maxY;
        let direction = 0;
        let distance = 0;
        let controlsCollapsed = false;
        let waitingForScrollInput = false;
        setCollapsed(false);

        const reveal = () => {
            if (hasKeyboardFocus(revealButtonRef.current)) focusControlsOnReveal.current = true;
            controlsCollapsed = false;
            distance = 0;
            direction = 0;
            setCollapsed(false);
        };
        revealControlsRef.current = () => {
            waitingForScrollInput = true;
            ignoreScrollUntilRef.current = Date.now() + EXPAND_GRACE_MS;
            previousY = measure().y;
            reveal();
        };
        const onScrollInput = event => {
            if (!waitingForScrollInput) return;
            if (event.type === 'wheel' && Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
            if (event.type === 'keydown'
                && !['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)) return;
            waitingForScrollInput = false;
            previousY = measure().y;
        };
        const canCollapse = ({ maxY }) => !selecting && !filterOpen
            // The dock's permanent bottom reservation does not make a short
            // list eligible to minimize its navigation controls.
            && maxY > controls.offsetHeight + COLLAPSE_DISTANCE;
        const onScroll = () => {
            const position = measure();
            const delta = position.y - previousY;
            previousY = position.y;
            if (!canCollapse(position) || position.y <= 1 || hasKeyboardFocus(controls)) {
                previousMaxY = position.maxY;
                reveal();
                return;
            }
            if (position.maxY !== previousMaxY) {
                previousMaxY = position.maxY;
                distance = 0;
                direction = 0;
                return;
            }
            if (waitingForScrollInput || Date.now() < ignoreScrollUntilRef.current || !delta) return;
            const nextDirection = Math.sign(delta);
            distance = nextDirection === direction ? distance + Math.abs(delta) : Math.abs(delta);
            direction = nextDirection;
            if (controlsCollapsed && direction < 0 && distance >= EXPAND_DISTANCE) {
                reveal();
            } else if (!controlsCollapsed && position.y > COLLAPSE_MIN_Y && direction > 0 && distance >= COLLAPSE_DISTANCE) {
                controlsCollapsed = true;
                distance = 0;
                setCollapsed(true);
            }
        };
        const onResize = () => {
            const position = measure();
            previousY = position.y;
            previousMaxY = position.maxY;
            distance = 0;
            direction = 0;
            if (!canCollapse(position) || position.y <= 1) reveal();
        };
        // The emoji popup and modal drawers have separate scroll owners.
        const removeScrollListener = addAppScrollListener(onScroll);
        window.addEventListener('resize', onResize);
        const inputEvents = ['pointerdown', 'wheel', 'keydown'];
        inputEvents.forEach(type => window.addEventListener(type, onScrollInput, { passive: true }));
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onResize);
        observer?.observe(getAppScrollElement());
        observer?.observe(controls);
        const content = headerRef.current.closest('.savedTracksContent');
        if (content) observer?.observe(content);
        return () => {
            revealControlsRef.current = null;
            removeScrollListener();
            window.removeEventListener('resize', onResize);
            inputEvents.forEach(type => window.removeEventListener(type, onScrollInput));
            observer?.disconnect();
        };
    }, [contentType, listId, selecting, filterOpen]);

    const filterLabel = listName || 'All';
    const contextLabel = `${contentType}, ${listName || 'All saved'}`;
    return <header ref={headerRef} className={`savedBrowsingHeader${collapsed ? ' savedBrowsingHeaderCollapsed' : ''}`}>
        <div className={`savedTracksHeader${selecting ? ' savedTracksHeaderSelecting' : ''}`}>
            {onBack && !selecting && <button type='button' className='savedBrowsingBack' aria-label='Back to Library' onClick={onBack}>
                <svg viewBox='0 0 24 24' aria-hidden='true'><path d='m14.5 5-7 7 7 7' /></svg>
            </button>}
            <div className='savedBrowsingTitle'>
                {!selecting ? <h1 className='largeHeading'>Saved</h1>
                    : <h1 className='savedSelectionTitle' aria-live='polite' aria-atomic='true'>
                        {selectionCount ? `${selectionCount} selected` : `Select ${contentType.toLowerCase()}`}
                    </h1>}
            </div>
            {actions}
        </div>
        <div className='savedBrowsingControlsViewport' data-selecting={selecting || undefined}>
            <button ref={revealButtonRef} type='button' className='savedBrowsingRevealButton' aria-hidden={!collapsed}
                aria-expanded={!collapsed} aria-controls={controlsId}
                aria-label={`Show Saved controls: ${contextLabel}`}
                tabIndex={collapsed ? 0 : -1}
                onClick={event => {
                    focusControlsOnReveal.current = event.detail === 0;
                    revealControlsRef.current?.();
                }}>
                <span className='savedBrowsingCompactSurface'><span>{contentType}</span><span aria-hidden='true'>·</span><span>{filterLabel}</span></span>
            </button>
            <div ref={controlsRef} id={controlsId} className='savedBrowsingControls' aria-hidden={collapsed} inert={collapsed ? '' : undefined}>
                <fieldset className='savedBrowsingControlsGroup' disabled={controlsDisabled}>
                    {children}
                    <SavedEmojiFilter lists={savedLists || []} selectedId={listId} selectedName={listName}
                        onChange={onListChange} open={filterOpen} onOpenChange={changeFilterOpen} disabled={controlsDisabled} />
                </fieldset>
            </div>
        </div>
    </header>;
}
